import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BillingAnalyticsPanel = ({ billingRecords, billingStatus }) => {
  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)}`;
  };

  // Prepare chart data
  const chartData = billingRecords?.slice(0, 12)?.reverse()?.map(record => ({
    date: new Date(record?.billing_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    amount: parseFloat(record?.total_amount || 0),
    status: record?.payment_status
  }));

  const paymentStatusBreakdown = {
    completed: billingRecords?.filter(r => r?.payment_status === 'completed')?.length || 0,
    pending: billingRecords?.filter(r => r?.payment_status === 'pending')?.length || 0,
    failed: billingRecords?.filter(r => r?.payment_status === 'failed')?.length || 0,
    cancelled: billingRecords?.filter(r => r?.payment_status === 'cancelled')?.length || 0
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
        <Icon name="BarChart3" size={20} color="var(--color-primary)" />
        Billing Analytics
      </h2>

      {/* Payment Status Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-3 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Completed</span>
            <Icon name="CheckCircle" size={14} color="var(--color-success)" />
          </div>
          <p className="text-xl font-bold text-success">{paymentStatusBreakdown?.completed}</p>
        </div>
        <div className="p-3 bg-warning/5 border border-warning/20 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Pending</span>
            <Icon name="Clock" size={14} color="var(--color-warning)" />
          </div>
          <p className="text-xl font-bold text-warning">{paymentStatusBreakdown?.pending}</p>
        </div>
        <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Failed</span>
            <Icon name="XCircle" size={14} color="var(--color-destructive)" />
          </div>
          <p className="text-xl font-bold text-destructive">{paymentStatusBreakdown?.failed}</p>
        </div>
        <div className="p-3 bg-muted/50 border border-border rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Cancelled</span>
            <Icon name="Ban" size={14} className="text-muted-foreground" />
          </div>
          <p className="text-xl font-bold text-muted-foreground">{paymentStatusBreakdown?.cancelled}</p>
        </div>
      </div>

      {/* Billing Trend Chart */}
      {chartData?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-3">Payment History (Last 12 Transactions)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="date" stroke="var(--color-muted-foreground)" fontSize={12} />
              <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
                formatter={(value) => formatCurrency(value)}
              />
              <Bar dataKey="amount" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Billing Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Next Billing Date</span>
            <Icon name="Calendar" size={16} color="var(--color-primary)" />
          </div>
          <p className="text-lg font-semibold text-foreground">
            {billingStatus?.next_billing_date
              ? new Date(billingStatus?.next_billing_date)?.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })
              : 'Not scheduled'}
          </p>
        </div>
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Grace Period</span>
            <Icon name="Clock" size={16} color="var(--color-primary)" />
          </div>
          <p className="text-lg font-semibold text-foreground">
            {billingStatus?.payment_grace_period_days || 0} days
          </p>
        </div>
      </div>
    </div>
  );
};

export default BillingAnalyticsPanel;